/**
 * AnalyticsIntegrationContent
 *
 * Reusable content component for analytics settings.
 * Shows analytics integrations list for project-level configuration.
 * Used by both AnalyticsSettingsPage and IntegrationsPage.
 *
 * Note: GDPR consent is now configured per-funnel in WebPlacementDialog.
 *
 * @module analytics-settings/components/AnalyticsIntegrationContent
 */

import { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Paper,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { WithGuideHelp } from '@modules/ui-components';
import { GuideService } from '@modules/firebase-core/services/GuideService';
import { GUIDE_ANCHORS } from '@modules/guides/anchors/guideAnchorRegistry';
import { useAnalyticsSettings } from '../hooks/useAnalyticsSettings';
import { IntegrationsList } from './IntegrationsList';
import { AddIntegrationDialog } from './AddIntegrationDialog';
import type { AnalyticsPlatform, PlatformConfig } from '../types/analytics.types';
import { ANALYTICS_SETTINGS_TEST_IDS } from '@modules/analytics-settings/testIds';

export interface AnalyticsIntegrationContentProps {
  /** Project ID to load analytics settings for */
  projectId: string;
  /** Whether to show header title and description */
  showHeader?: boolean;
}

/**
 * AnalyticsIntegrationContent Component
 *
 * Displays analytics configuration UI:
 * - List of configured analytics integrations
 * - Add/edit/delete integration functionality
 *
 * Note: GDPR consent is configured per-funnel (see WebPlacementDialog).
 */
export function AnalyticsIntegrationContent({
  projectId,
  showHeader = false,
}: AnalyticsIntegrationContentProps) {
  const guideService = useMemo(() => new GuideService(), []);
  const {
    settings,
    loading,
    error,
    saving,
    addIntegration,
    updateIntegration,
    deleteIntegration,
    toggleIntegration,
  } = useAnalyticsSettings(projectId);

  // Add integration dialog state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const hasIntegrations = settings.integrations.length > 0;

  // Handle add integration
  const handleAddIntegration = useCallback(
    async (platform: AnalyticsPlatform, name: string, config: PlatformConfig) => {
      await addIntegration(platform, name, config);
      setAddDialogOpen(false);
    },
    [addIntegration]
  );

  // Show loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 200,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Alert severity="error">
        Failed to load analytics settings: {error.message}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Optional Header with Add Button */}
      {showHeader && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Box>
            <Typography variant="h6" gutterBottom>
              Analytics
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configure analytics integrations for all web funnels in this project.
            </Typography>
          </Box>

          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setAddDialogOpen(true)}
            disabled={saving}
            size="small"
          >
            Add Integration
          </Button>
        </Box>
      )}

      {/* Integrations List. D-16: with zero integrations ONLY the centred empty state renders —
          the header row (title, Add, help icons) and the "automatically injected" line appear
          once a provider exists, so `analytics-add-integration-btn` and the `analyticsAddProvider`
          anchor are always exactly ONE node (empty-state button or header button). */}
      <Paper sx={{ p: 2 }} variant="outlined">
        {hasIntegrations && (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                <WithGuideHelp
                  guideKey={GUIDE_ANCHORS.analyticsIntegrationList.guideKey}
                  guideService={guideService}
                  data-testid={GUIDE_ANCHORS.analyticsIntegrationList.testId}
                  position="inline-end"
                >
                  Integrations
                </WithGuideHelp>
              </Typography>
              {!showHeader && (
                <WithGuideHelp
                  guideKey={GUIDE_ANCHORS.analyticsAddProvider.guideKey}
                  guideService={guideService}
                  data-testid={GUIDE_ANCHORS.analyticsAddProvider.testId}
                  position="top-right"
                >
                  <Button
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={() => setAddDialogOpen(true)}
                    disabled={saving}
                    size="small"
                    data-testid={ANALYTICS_SETTINGS_TEST_IDS.addBtn}
                  >
                    Add
                  </Button>
                </WithGuideHelp>
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              All enabled integrations are automatically injected into every published web funnel.
            </Typography>
          </>
        )}

        <IntegrationsList
          integrations={settings.integrations}
          onUpdate={updateIntegration}
          onDelete={deleteIntegration}
          onToggle={toggleIntegration}
          disabled={saving}
          projectId={projectId}
        />

        {!hasIntegrations && (
          <Box
            sx={{
              textAlign: 'center',
              py: 4,
              color: 'text.secondary',
            }}
          >
            <Typography variant="body2" gutterBottom>
              No analytics integrations configured yet.
            </Typography>
            <WithGuideHelp
              guideKey={GUIDE_ANCHORS.analyticsAddProvider.guideKey}
              guideService={guideService}
              data-testid={GUIDE_ANCHORS.analyticsAddProvider.testId}
              position="top-right"
              sx={{ mt: 1 }}
            >
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={() => setAddDialogOpen(true)}
                disabled={saving}
                size="small"
                data-testid={ANALYTICS_SETTINGS_TEST_IDS.addBtn}
              >
                Add Your First Integration
              </Button>
            </WithGuideHelp>
          </Box>
        )}
      </Paper>

      {/* Add Integration Dialog */}
      <AddIntegrationDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onAdd={handleAddIntegration}
        existingIntegrations={settings.integrations}
        projectId={projectId}
      />
    </Box>
  );
}

export default AnalyticsIntegrationContent;
